import type { StaffCreatedRequest } from "@lafayette/schemas";
import { useState } from "react";

import { rolesHold } from "../../access/capabilities";
import { useStaffRoles } from "../../access/use-staff-roles";
import { Button } from "../../ui/button";
import { Icon } from "../../ui/icon";
import { useOptionalHomeRuntime } from "../runtime";
import { ChooseCustomerDialog, type PickedCustomer, RequestFormDialog } from "./NewRequestDialogs";
import { EMPTY_VALUES, type FormValues } from "./form-model";

// "New request" on Home (CHT-2051, ADR 0018): the button, and the two windows behind it. The button is drawn only for a role that holds
// `request.create_for_customer` (a concierge or a supervisor), and only when the runtime has the client. That is presentation: the API checks
// the role and the customer again, and a refusal is shown inside the form.

export type CreatedRequest = Readonly<{ created: StaffCreatedRequest; customerName: string }>;

type Step = "closed" | "customer" | "form";

export function NewRequest({ onCreated }: Readonly<{ onCreated: (done: CreatedRequest) => void }>) {
  const runtime = useOptionalHomeRuntime();
  const api = runtime?.requestCreate;
  const { roles } = useStaffRoles();
  const [step, setStep] = useState<Step>("closed");
  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [values, setValues] = useState<FormValues>(EMPTY_VALUES);

  if (api === undefined || !rolesHold(roles, "request.create_for_customer")) return null;

  const close = () => {
    setStep("closed");
    setCustomer(null);
    setValues(EMPTY_VALUES);
  };

  return (
    <>
      <Button type="button" size="lg" onClick={() => setStep("customer")}>
        <Icon name="plus-sign" />
        New request
      </Button>
      {step === "customer" ? (
        <ChooseCustomerDialog
          onPick={(picked) => {
            setCustomer(picked);
            setStep("form");
          }}
          onClose={close}
        />
      ) : null}
      {step === "form" && customer !== null ? (
        <RequestFormDialog
          customer={customer}
          values={values}
          onValues={setValues}
          create={(body, idempotencyKey) =>
            api.createForCustomer(customer.id, body, { idempotencyKey })
          }
          onChangeCustomer={() => setStep("customer")}
          onDone={(created) => {
            onCreated({ created, customerName: customer.name });
            close();
          }}
          onClose={close}
        />
      ) : null}
    </>
  );
}
